import type { Gate } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import type { Ring } from '../../../geo/types';
import { archedBlock } from '../landmarks/fortress/fortressBlocks';
import { boxSolid, horizontalCap, pyramidOverRing } from '../solids';
import { defensiveMasonryFace } from './towerMasonry';

/** Single medieval gate-tower in the1538 painting, not the later moved timber gate
 * replaced in1636. The anchor is retained; height, passage and hidden sides are estimates. */
export const HEILIGEWEGSPOORT = {
  eaveY: 13,
  helmY: 14.65,
  cornerY: 14.15,
  finialY: 15.15,
  upperFootY: 4.8,
  springY: 2.6,
  passageRatio: 0.4,
} as const;

interface Styles {
  readonly wall: VertexStyle;
  readonly roof: VertexStyle;
  readonly dark: VertexStyle;
}

export function buildHeiligewegspoort(
  builder: MeshBuilder, frame: Frame, gate: Gate, styles: Styles
): void {
  const u = gate.depth / 2 - 0.04, v = gate.width / 2 - 0.04;
  const pass = gate.width * HEILIGEWEGSPOORT.passageRatio / 2;
  archedBlock(builder, styles.wall, frame, {
    u: [-u, u], v: [-v, v], foot: -0.3, top: HEILIGEWEGSPOORT.upperFootY,
    passage: { v: [-pass, pass], springing: HEILIGEWEGSPOORT.springY }, openTop: true,
  });
  const ring: Ring = [[-u, -v], [u, -v], [u, v], [-u, v]];
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const windows = [2, 5.6].map((y) => ({ x: width / 2 - 0.27, y,
      width: 0.54, height: 1.1, cuts: [] }));
    defensiveMasonryFace(builder, styles.wall, frame, a, b,
      HEILIGEWEGSPOORT.upperFootY, HEILIGEWEGSPOORT.eaveY - HEILIGEWEGSPOORT.upperFootY,
      windows, styles.dark);
  });
  horizontalCap(builder, styles.wall, frame, ring, HEILIGEWEGSPOORT.eaveY, true);
  // The low central dark helm and four separate corner pinnacles are visible in the
  // painting/woodcut. Their exact sections and concealed junctions are not surveyed.
  const roofRing: Ring = [[-u + 0.28, -v + 0.28], [u - 0.28, -v + 0.28],
    [u - 0.28, v - 0.28], [-u + 0.28, v - 0.28]];
  pyramidOverRing(builder, styles, frame, roofRing, HEILIGEWEGSPOORT.eaveY,
    { plan: [0, 0], y: HEILIGEWEGSPOORT.helmY });
  for (const sideU of [-1, 1]) for (const sideV of [-1, 1]) {
    const x = sideU * (u - 0.23), z = sideV * (v - 0.23);
    boxSolid(builder, styles.wall, frame, [x - 0.2, 12.25, -z - 0.2],
      [x + 0.2, HEILIGEWEGSPOORT.cornerY, -z + 0.2], { top: false, bottom: false });
    const cap: Ring = [[x - 0.2, z - 0.2], [x + 0.2, z - 0.2],
      [x + 0.2, z + 0.2], [x - 0.2, z + 0.2]];
    pyramidOverRing(builder, styles, frame, cap, HEILIGEWEGSPOORT.cornerY,
      { plan: [x, z], y: HEILIGEWEGSPOORT.finialY });
  }
}
